import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import * as XLSX from 'xlsx';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
  }, [activeTab]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stats/dashboard');
    } catch (error) {
      console.error('Error loading stats:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', fontFamily: 'Inter, -apple-system, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <div style={{ color: '#ffffff' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        onLogout={handleLogout}
        user={user}
      />
      
      <MainContent 
        activeTab={activeTab} 
        message={message}
        setMessage={setMessage}
      />
    </div>
  );
};

const Sidebar = ({ activeTab, setActiveTab, onLogout, user }) => {
  const navigate = useNavigate();

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
    { id: 'sessions', label: 'Sessions', icon: 'fa-calendar-alt' },
    { id: 'pdvs', label: 'Points de Vente', icon: 'fa-store' },
    { id: 'stocks', label: 'Stocks', icon: 'fa-boxes', external: true },
    { id: 'daily-quotas', label: 'Quotas Journaliers', icon: 'fa-calendar-day', external: true },
    { id: 'prizes', label: 'Lots', icon: 'fa-gift' },
    { id: 'injection', label: 'Injection', icon: 'fa-syringe' },
    { id: 'participants', label: 'Participants', icon: 'fa-users' },
    { id: 'users', label: 'Utilisateurs', icon: 'fa-user-cog' },
  ];

  const handleMenuClick = (item) => {
    if (item.external) {
      navigate(`/${item.id}`);
    } else {
      setActiveTab(item.id);
    }
  };

  return (
    <div style={{
      width: '250px',
      background: 'rgba(26, 26, 46, 0.95)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      borderRight: '1px solid rgba(255, 215, 0, 0.2)'
    }}>
      <div style={{
        padding: '24px',
        borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
      }}>
        <h3 style={{ color: '#FFD700', margin: 0, fontSize: '20px', fontWeight: 600 }}>
          <i className="fas fa-bolt" style={{ marginRight: '8px' }}></i>REAKTOR
        </h3>
      </div>
      <div style={{ padding: '16px' }}>
        {menuItems.map(item => (
          <button
            key={item.id}
            onClick={() => handleMenuClick(item)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              background: activeTab === item.id ? 'rgba(255, 215, 0, 0.2)' : 'transparent',
              textAlign: 'left',
              color: activeTab === item.id ? '#FFD700' : 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              borderRadius: '8px',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <i className={item.icon} style={{ width: '20px', textAlign: 'center' }}></i>
            {item.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 'auto', padding: '16px', borderTop: '1px solid rgba(255, 215, 0, 0.2)' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1a1a2e',
              fontSize: '18px',
              fontWeight: 600
            }}>
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>{user?.full_name || 'Admin'}</div>
              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>{user?.role || ''}</div>
            </div>
          </div>
        </div>
        <button
          onClick={onLogout}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'transparent',
            color: '#ff4d4f',
            border: '1px solid #ff4d4f',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 77, 79, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
          }}
        >
          <i className="fas fa-sign-out-alt"></i>
          Déconnexion
        </button>
      </div>
    </div>
  );
};

const MainContent = ({ activeTab, message, setMessage }) => {
  const [stats, setStats] = useState(null);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedPOS, setSelectedPOS] = useState('all');

  const loadStats = async (cityId = null, dateFrom = null, pdvId = null) => {
    try {
      const params = {};
      if (cityId && cityId !== 'all') params.city_id = parseInt(cityId);
      if (dateFrom && dateFrom !== 'all') params.date_from = dateFrom;
      if (pdvId && pdvId !== 'all') params.pdv_id = parseInt(pdvId);

      const response = await api.get('/admin/stats/dashboard', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' });
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadStats(selectedCity, selectedDate, selectedPOS);
    }
  }, [activeTab, selectedCity, selectedDate, selectedPOS]);

  if (activeTab === 'sessions') {
    return (
      <SessionsContent message={message} setMessage={setMessage} />
    );
  }

  if (activeTab === 'pdvs') {
    return (
      <PDVsContent message={message} setMessage={setMessage} />
    );
  }

  if (activeTab === 'prizes') {
    return (
      <PrizesContent message={message} setMessage={setMessage} />
    );
  }

  if (activeTab === 'injection') {
    return (
      <InjectionContent message={message} setMessage={setMessage} />
    );
  }

  if (activeTab === 'participants') {
    return <ParticipantsContent message={message} setMessage={setMessage} />;
  }

  if (activeTab === 'users') {
    return (
      <UsersContent message={message} setMessage={setMessage} />
    );
  }

  if (activeTab !== 'dashboard') {
    return (
      <div style={{ flex: 1, padding: '32px', background: 'transparent' }}>
        <div style={{
          background: 'rgba(26, 26, 46, 0.9)',
          borderRadius: '16px',
          padding: '80px 40px',
          textAlign: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
          border: '2px solid rgba(255, 215, 0, 0.3)'
        }}>
          <i className="fas fa-tools" style={{ fontSize: '64px', color: '#FFD700', marginBottom: '24px' }}></i>
          <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '20px', fontWeight: 600 }}>
            Module en développement
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', lineHeight: 1.6 }}>
            Ce module sera bientôt disponible.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <DashboardHeader
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedPOS={selectedPOS}
        setSelectedPOS={setSelectedPOS}
        cities={stats?.cities || []}
        stats={stats}
      />

      <KPISection stats={stats} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <PrizeDistributionSection stats={stats} />
        <ParticipationModeSection stats={stats} />
      </div>

      <PerformanceSection stats={stats} />

      <CommercialPressureSection stats={stats} />
    </div>
  );
};

const DashboardHeader = ({ selectedCity, setSelectedCity, selectedDate, setSelectedDate, selectedPOS, setSelectedPOS, cities, stats }) => {
  // Générer la liste des PDV uniques depuis les stats
  const pdvList = stats?.by_pos ? [...new Set(stats.by_pos.map(p => p.name))] : [];

  // Convertir le preset de date en date réelle
  const getFromDate = (preset) => {
    if (preset === 'all' || !preset) return null;
    const today = new Date();
    if (preset === 'today') {
      return today.toISOString().split('T')[0];
    } else if (preset === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return weekAgo.toISOString().split('T')[0];
    } else if (preset === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return monthAgo.toISOString().split('T')[0];
    }
    return preset;
  };

  const handleDateChange = (e) => {
    setSelectedDate(getFromDate(e.target.value) || e.target.value);
  };

  return (
  <div style={{
    background: 'rgba(26, 26, 46, 0.9)',
    borderRadius: '16px',
    padding: '24px 32px',
    marginBottom: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '2px solid rgba(255, 215, 0, 0.3)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fas fa-bolt" style={{ color: '#1a1a2e', fontSize: '20px' }}></i>
      </div>
      <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
        Dashboard
      </h1>
    </div>

    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <i className="fas fa-filter" style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}></i>
        <select
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          style={{
            padding: '8px 16px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#ffffff',
            background: 'rgba(26, 26, 46, 0.8)',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
        >
          <option value="all">Toutes les villes</option>
          {cities.map(city => (
            <option key={city.id} value={city.id}>{city.name}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <select
          value={typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/) ? 'custom' : selectedDate}
          onChange={handleDateChange}
          style={{
            padding: '8px 16px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#ffffff',
            background: 'rgba(26, 26, 46, 0.8)',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
        >
          <option value="all">Toutes les dates</option>
          <option value="today">Aujourd'hui</option>
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
          <option value="custom">Date spécifique</option>
        </select>
        {(typeof selectedDate === 'string' && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) && (
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: '8px 16px',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#ffffff',
              background: 'rgba(26, 26, 46, 0.8)',
              cursor: 'pointer',
              outline: 'none'
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <select
          value={selectedPOS}
          onChange={(e) => setSelectedPOS(e.target.value)}
          style={{
            padding: '8px 16px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#ffffff',
            background: 'rgba(26, 26, 46, 0.8)',
            cursor: 'pointer',
            outline: 'none',
            transition: 'border-color 0.2s'
          }}
        >
          <option value="all">Tous les points de vente</option>
          {pdvList.map(pdv => (
            <option key={pdv} value={pdv}>{pdv}</option>
          ))}
        </select>
      </div>
    </div>
  </div>
  );
};

const KPISection = ({ stats }) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
    gap: '24px',
    marginBottom: '24px'
  }}>
    <KPICard
      title="Participants"
      value={stats?.summary?.total_participations || 0}
      icon="fa-user-group"
      color="#1890ff"
    />
    <KPICard
      title="Gagnants"
      value={stats?.summary?.total_winners || 0}
      icon="fa-medal"
      color="#52c41a"
    />
    <KPICard
      title="Lots Gagnés"
      value={stats?.summary?.total_prizes || 0}
      icon="fa-gift"
      color="#faad14"
    />
    <KPICard
      title="Bouteilles Vendues"
      value={stats?.summary?.total_bottles || 0}
      icon="fa-wine-bottle"
      color="#eb2f96"
    />
    <KPICard
      title="Points de Vente Actifs"
      value={stats?.summary?.active_points || 0}
      icon="fa-store"
      color="#722ed1"
    />
  </div>
);

const KPICard = ({ title, value, icon, color }) => (
  <div style={{
    background: 'rgba(26, 26, 46, 0.9)',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    transition: 'all 0.3s',
    border: '2px solid rgba(255, 215, 0, 0.3)'
  }}
  onMouseEnter={(e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.2)';
    e.target.style.borderColor = 'rgba(255, 215, 0, 0.5)';
  }}
  onMouseLeave={(e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
    e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
  }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
      <div style={{
        width: '52px',
        height: '52px',
        background: color + '25',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '24px' }}></i>
      </div>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', marginBottom: '8px', lineHeight: 1.2 }}>
      {value.toLocaleString()}
    </div>
    <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
      {title}
    </div>
  </div>
);

const PrizeDistributionSection = ({ stats }) => (
  <div style={{
    background: 'rgba(26, 26, 46, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '2px solid rgba(255, 215, 0, 0.3)'
  }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'rgba(114, 46, 209, 0.25)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fa-solid fa-gift" style={{ color: '#722ed1', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        Répartition des Lots
      </h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {stats?.prizes && Object.entries(stats.prizes).map(([prizeType, data], idx) => {
        const total = data.won;
        return (
          <div key={prizeType} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            background: 'rgba(26, 26, 46, 0.5)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 215, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: getColor(idx) + '25',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="fa-solid fa-medal" style={{ color: getColor(idx), fontSize: '20px' }}></i>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{prizeType}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: getColor(idx) }}>{total}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const ParticipationModeSection = ({ stats }) => (
  <div style={{
    background: 'rgba(26, 26, 46, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '2px solid rgba(255, 215, 0, 0.3)'
  }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'rgba(24, 144, 255, 0.25)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fa-solid fa-share-nodes" style={{ color: '#1890ff', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        Mode de Participation
      </h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <ModeCard
        icon="fab fa-whatsapp"
        label="WhatsApp"
        value={stats?.engagement?.whatsapp || 0}
        rate={stats?.engagement?.whatsapp_rate || 0}
        color="#25D366"
      />
      <ModeCard
        icon="fa-solid fa-mobile-screen"
        label="Direct"
        value={stats?.engagement?.direct || 0}
        rate={stats?.engagement?.direct_rate || 0}
        color="#1890ff"
      />
    </div>
  </div>
);

const ModeCard = ({ icon, label, value, rate, color }) => (
  <div style={{
    padding: '16px',
    background: 'rgba(26, 26, 46, 0.5)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 215, 0, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '44px',
        height: '44px',
        background: color + '25',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className={icon} style={{ color: color, fontSize: icon === 'fab fa-whatsapp' ? '26px' : '20px' }}></i>
      </div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{label}</div>
        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '2px' }}>
          {rate}% du total
        </div>
      </div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: '20px', fontWeight: 700, color: color }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>Participations</div>
    </div>
  </div>
);

const PerformanceSection = ({ stats }) => (
  <div style={{
    background: 'rgba(26, 26, 46, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '2px solid rgba(255, 215, 0, 0.3)'
  }}>
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px',
        height: '40px',
        background: 'rgba(250, 173, 20, 0.25)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="fa-solid fa-store" style={{ color: '#faad14', fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        Performance par Point de Vente
      </h3>
    </div>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Point de Vente</th>
            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Ville</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Inscrits</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Gagnants</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Perdants</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Bouteilles</th>
            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Taux de gagnants</th>
          </tr>
        </thead>
        <tbody>
          {stats?.by_pos && stats.by_pos.map((pos, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
              <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px', fontWeight: 500 }}>{pos.name}</td>
              <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>{pos.city}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#1890ff', fontSize: '14px', fontWeight: 600 }}>{pos.inscrits || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#52c41a', fontSize: '14px', fontWeight: 600 }}>{pos.gagnants || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#ff7875', fontSize: '14px', fontWeight: 600 }}>{pos.perdants || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center', color: '#faad14', fontSize: '14px', fontWeight: 600 }}>{pos.bouteilles || 0}</td>
              <td style={{ padding: '16px', textAlign: 'center' }}>
                <span style={{
                  padding: '6px 16px',
                  borderRadius: '20px',
                  background: pos.performance >= 80 ? 'rgba(82, 196, 26, 0.2)' : pos.performance >= 50 ? 'rgba(250, 173, 20, 0.2)' : 'rgba(255, 77, 79, 0.2)',
                  color: pos.performance >= 80 ? '#95de64' : pos.performance >= 50 ? '#FFD700' : '#ff7875',
                  fontWeight: 600,
                  fontSize: '14px',
                  display: 'inline-block'
                }}>
                  {pos.performance || 0}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Composant pour afficher les détails horaires d'une cellule de date
const DateCellDetail = ({ dateData, dateStr, total }) => {
  const [showDetails, setShowDetails] = useState(false);

  // Trouver l'heure de pointe pour cette date
  const peakHour = dateData?.hourly?.reduce((max, h) => h.bottles > (max?.bottles || 0) ? h : max, null);

  // Filtrer seulement les heures avec des ventes
  const hoursWithSales = dateData?.hourly?.filter(h => h.bottles > 0) || [];

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      <span style={{
        color: '#fff',
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: '4px',
        background: 'rgba(255, 215, 0, 0.2)',
        cursor: 'help'
      }}>
        {total}
      </span>

      {showDetails && hoursWithSales.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          marginTop: '8px',
          background: 'rgba(26, 26, 46, 0.98)',
          border: '1px solid rgba(255, 215, 0, 0.4)',
          borderRadius: '8px',
          padding: '12px',
          minWidth: '280px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          pointerEvents: 'none'
        }}>
          <div style={{ marginBottom: '8px', borderBottom: '1px solid rgba(255, 215, 0, 0.2)', paddingBottom: '8px' }}>
            <div style={{ color: '#FFD700', fontSize: '13px', fontWeight: 600 }}>
              {new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>Total: {total} bouteilles</div>
          </div>

          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {hoursWithSales.map((h, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 0',
                borderBottom: idx < hoursWithSales.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                background: h.hour === peakHour?.hour ? 'rgba(255, 215, 0, 0.15)' : 'transparent'
              }}>
                <span style={{ color: h.hour === peakHour?.hour ? '#FFD700' : '#8c8c8c', fontSize: '12px' }}>
                  {h.hour}h
                </span>
                <span style={{
                  color: h.hour === peakHour?.hour ? '#FFD700' : '#fff',
                  fontWeight: h.hour === peakHour?.hour ? 600 : 400,
                  fontSize: '12px'
                }}>
                  {h.bottles} bouteilles
                </span>
              </div>
            ))}
          </div>

          {peakHour && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 215, 0, 0.2)', fontSize: '11px', color: '#8c8c8c' }}>
              <i className="fa-solid fa-star" style={{ color: '#FFD700', marginRight: '4px' }}></i>
              Pic: {peakHour.hour}h ({peakHour.bottles} bouteilles)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CommercialPressureSection = ({ stats }) => {
  const [hourlyData, setHourlyData] = useState(null);
  const [reservesData, setReservesData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Liste des heures disponibles (8h à 20h)
  const availableHours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
  const [selectedHour, setSelectedHour] = useState(null); // null = toutes les heures

  const loadDetailedStats = async () => {
    if (loadingDetails) return;
    setLoadingDetails(true);
    try {
      // Charger les données horaires (une seule fois)
      if (!hourlyData) {
        const hourlyRes = await api.get('/admin/stats/hourly');
        setHourlyData(hourlyRes.data);
      }

      // Charger les données d'injections depuis l'API dashboard
      const statsRes = await api.get('/admin/stats/dashboard');
      console.log('Injection stats from API:', statsRes.data.injection_stats);
      console.log('Full stats response:', statsRes.data);
      setReservesData(statsRes.data.injection_stats || []);
    } catch (error) {
      console.error('Error loading detailed stats:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    loadDetailedStats();
  }, [stats]);

  return (
    <div style={{
      background: 'rgba(26, 26, 46, 0.9)',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      border: '2px solid rgba(255, 215, 0, 0.3)'
    }}>
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: 'rgba(19, 194, 194, 0.25)',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="fa-solid fa-chart-line" style={{ color: '#13c2c2', fontSize: '18px' }}></i>
        </div>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
          Pression Commerciale
        </h3>
      </div>

      {/* Bouteilles par date et par point de vente */}
      <div style={{ marginBottom: '30px' }}>
        <h4 style={{ color: '#FFD700', fontSize: '15px', fontWeight: 600, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fa-solid fa-calendar-days"></i>
          Bouteilles vendues par date et par point de vente
        </h4>

        {/* Filtre par heure */}
        <div style={{ marginBottom: '15px', padding: '12px', background: 'rgba(255, 215, 0, 0.05)', borderRadius: '8px', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: '#8c8c8c', fontSize: '13px', fontWeight: 500 }}>Filtre horaire :</span>
            <select
              value={selectedHour || ''}
              onChange={(e) => setSelectedHour(e.target.value ? parseInt(e.target.value) : null)}
              style={{ padding: '6px 12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '6px', background: 'rgba(26, 26, 46, 0.8)', color: '#fff', fontSize: '13px', minWidth: '120px' }}
            >
              <option value="">Toutes les heures</option>
              {availableHours.map(hour => (
                <option key={hour} value={hour}>{hour}h</option>
              ))}
            </select>
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
              {selectedHour !== null ? `Affichage: ${selectedHour}h uniquement` : `Affichage: toutes les heures (8h-20h)`}
            </span>
          </div>
        </div>

        {/* Tableau par PDV et par date avec détails horaires */}
        {(() => {
          if (!hourlyData?.by_pdv || hourlyData.by_pdv.length === 0) {
            return <div style={{ padding: '20px', textAlign: 'center', color: '#8c8c8c' }}>Aucune donnée disponible</div>;
          }

          const allDates = hourlyData.all_dates || [];

          // Filtrer les données horaires selon l'heure sélectionnée (ou toutes les heures si null)
          const filteredPdvData = hourlyData.by_pdv.map(pdv => {
            const filteredDates = {};
            let totalBottles = 0;

            Object.keys(pdv.dates || {}).forEach(dateStr => {
              const dateData = pdv.dates[dateStr];
              // Filtrer par heure sélectionnée ou toutes les heures de 8h à 20h
              const filteredHourly = dateData.hourly.filter(h =>
                h.hour >= 8 && h.hour <= 20 && (selectedHour === null || h.hour === selectedHour)
              );
              const dayTotal = filteredHourly.reduce((sum, h) => sum + h.bottles, 0);

              if (dayTotal > 0) {
                filteredDates[dateStr] = {
                  ...dateData,
                  hourly: filteredHourly,
                  total: dayTotal
                };
                totalBottles += dayTotal;
              }
            });

            return {
              ...pdv,
              dates: filteredDates,
              total_bottles: totalBottles,
              sorted_dates: Object.keys(filteredDates).sort()
            };
          }).filter(pdv => pdv.total_bottles > 0); // Ne garder que les PDVs avec des données dans le filtre

          // Récupérer toutes les dates qui ont encore des données après filtrage
          const filteredDates = Array.from(new Set(filteredPdvData.flatMap(pdv => Object.keys(pdv.dates)))).sort();

          return (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.3)' }}>
                    <th style={{ padding: '10px', textAlign: 'left', color: '#FFD700', position: 'sticky', left: 0, background: 'rgba(26, 26, 46, 0.9)', zIndex: 10, minWidth: '150px' }}>Point de vente</th>
                    {filteredDates.map(dateStr => {
                      const date = new Date(dateStr);
                      return (
                        <th key={dateStr} style={{ padding: '8px', textAlign: 'center', color: '#FFD700', minWidth: '100px', fontSize: '11px' }}>
                          <div>{date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</div>
                          <div style={{ fontSize: '9px', color: 'rgba(255, 215, 0, 0.6)' }}>{date.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                        </th>
                      );
                    })}
                    <th style={{ padding: '10px', textAlign: 'center', color: '#FFD700', minWidth: '80px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPdvData.map((pdv, pdvIdx) => (
                    <tr key={pdvIdx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <td style={{ padding: '10px', position: 'sticky', left: 0, background: 'rgba(26, 26, 46, 0.9)', zIndex: 5, borderRight: '1px solid rgba(255, 215, 0, 0.2)' }}>
                        <div style={{ color: '#fff', fontWeight: 500 }}>{pdv.pdv_name}</div>
                        <div style={{ fontSize: '10px', color: '#8c8c8c' }}>{pdv.city}</div>
                      </td>
                      {filteredDates.map(dateStr => {
                        const dateData = pdv.dates[dateStr];
                        const total = dateData?.total || 0;
                        const hasHourlyData = dateData?.hourly?.some(h => h.bottles > 0);
                        return (
                          <td key={dateStr} style={{
                            padding: '6px',
                            textAlign: 'center',
                            cursor: hasHourlyData ? 'pointer' : 'default',
                            background: total > 0 ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                          }}>
                            {total > 0 ? (
                              <DateCellDetail dateData={dateData} dateStr={dateStr} total={total} />
                            ) : (
                              <span style={{ color: '#3a3a4a' }}>-</span>
                            )}
                          </td>
                        );
                      })}
                      <td style={{ padding: '10px', textAlign: 'center', fontWeight: 700, color: '#FFD700', borderLeft: '1px solid rgba(255, 215, 0, 0.2)' }}>
                        {pdv.total_bottles}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Légende */}
        {hourlyData?.by_pdv && (
          <div style={{ marginTop: '10px', display: 'flex', gap: '20px', fontSize: '12px', color: '#8c8c8c' }}>
            <span><i className="fa-solid fa-hand-pointer" style={{ marginRight: '5px' }}></i>Survolez une cellule pour voir le détail par heure ({selectedHour !== null ? `${selectedHour}h sélectionnée` : 'toutes les heures (8h-20h)'})</span>
          </div>
        )}
      </div>

      {/* État des réserves */}
      <div>
        <h4 style={{ color: '#FFD700', fontSize: '15px', fontWeight: 600, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="fa-solid fa-warehouse"></i>
          État des réserves (Injections vs Gagnés)
        </h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.3)' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#FFD700' }}>Point de vente</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#FFD700' }}>Injections</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#FFD700' }}>Gagnés</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#FFD700' }}>En attente</th>
              </tr>
            </thead>
            <tbody>
              {loadingDetails ? (
                <tr>
                  <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#8c8c8c' }}>Chargement...</td>
                </tr>
              ) : reservesData && reservesData.length > 0 ? (
                reservesData.map((data) => (
                  <tr key={data.pdv_id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <td style={{ padding: '12px', color: '#fff' }}>{data.pdv_name}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#fff' }}>{data.injected}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#52c41a' }}>{data.won}</td>
                    <td style={{ padding: '12px', textAlign: 'center', color: data.pending > 5 ? '#ff4d4f' : '#faad14' }}>{data.pending}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#8c8c8c' }}>Aucune injection programmée</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PressureCard = ({ title, value, suffix, target, color, progress }) => (
  <div style={{
    padding: '20px',
    borderRadius: '12px',
    background: 'rgba(26, 26, 46, 0.5)',
    border: '1px solid rgba(255, 215, 0, 0.2)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>{title}</div>
      <div style={{
        width: '32px',
        height: '32px',
        background: color + '30',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {title === 'Taux de Conversion' && <i className="fa-solid fa-percent" style={{ color: color, fontSize: '14px' }}></i>}
        {title === 'Engagement Client' && <i className="fa-solid fa-bolt" style={{ color: color, fontSize: '14px' }}></i>}
        {title === 'Ventes Moyennes/Jour' && <i className="fa-solid fa-chart-simple" style={{ color: color, fontSize: '14px' }}></i>}
        {title === 'ROI Campagne' && <i className="fa-solid fa-coins" style={{ color: color, fontSize: '14px' }}></i>}
      </div>
    </div>
    <div style={{ fontSize: '32px', fontWeight: 700, color: '#ffffff', marginBottom: '12px', lineHeight: 1 }}>
      {value}<span style={{ color: color }}>{suffix}</span>
    </div>
    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '12px' }}>Objectif: {target}</div>
    <div style={{ height: '6px', background: 'rgba(255, 215, 0, 0.2)', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{
        height: '100%',
        width: `${Math.min(progress, 100)}%`,
        background: color,
        borderRadius: '3px',
        transition: 'width 0.3s'
      }}></div>
    </div>
  </div>
);

const getColor = (idx) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2'];
  return colors[idx % colors.length];
};

const getGainModeBadge = (gainMode) => {
  const modeConfig = {
    auto: { label: 'Automatique', color: '#95de64', bg: 'rgba(82, 196, 26, 0.2)', icon: 'fa-magic' },
    ratio: { label: 'Ratio', color: '#FFD700', bg: 'rgba(24, 144, 255, 0.2)', icon: 'fa-calculator' },
    force: { label: 'Forcé', color: '#FFD700', bg: 'rgba(250, 173, 20, 0.2)', icon: 'fa-hand-pointer' },
    pourcentage: { label: 'Pourcentage', color: '#FFD700', bg: 'rgba(250, 84, 28, 0.2)', icon: 'fa-percent' },
    POURCENTAGE: { label: 'Pourcentage', color: '#FFD700', bg: 'rgba(250, 84, 28, 0.2)', icon: 'fa-percent' }
  };
  const config = modeConfig[gainMode] || modeConfig.auto;
  return (
    <span style={{
      padding: '6px 12px',
      borderRadius: '20px',
      background: config.bg,
      color: config.color,
      fontSize: '12px',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      <i className={`fas ${config.icon}`}></i>
      {config.label}
    </span>
  );
};

const SessionsContent = ({ message, setMessage }) => {
  const [sessions, setSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [filters, setFilters] = useState({ pdv: 'all', status: 'all', date: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      // En cas d'erreur, utiliser des données mockées correctes
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    if (filters.pdv !== 'all' && session.pdv?.name !== filters.pdv) return false;
    if (filters.status !== 'all' && session.status !== filters.status) return false;
    if (filters.date) {
      const sessionDate = new Date(session.start_time);
      const sessionDateStr = sessionDate.getFullYear() + '-' +
        String(sessionDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(sessionDate.getDate()).padStart(2, '0');
      if (sessionDateStr !== filters.date) return false;
    }
    return true;
  });

  const handleCreateSession = () => {
    setSelectedSession(null);
    setShowModal(true);
  };

  const handleViewSession = (session) => {
    setSelectedSession(session);
    setShowModal(true);
  };

  const handleViewStock = (session) => {
    setSelectedSession(session);
    setShowStockModal(true);
  };

  const handleToggleSession = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    const newStatus = session.status === 'active' ? 'pending' : 'active';

    try {
      if (newStatus === 'active') {
        await api.put(`/admin/sessions/${sessionId}/activate`);
      } else {
        await api.put(`/admin/sessions/${sessionId}/deactivate`);
      }
      setSessions(sessions.map(s => s.id === sessionId ? { ...s, status: newStatus } : s));
    } catch (error) {
      console.error('Error toggling session:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette session ? Cette action supprimera aussi toutes les allocations de stock et participations associées.')) {
      return;
    }

    try {
      await api.delete(`/admin/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      setMessage({ type: 'success', text: 'Session supprimée avec succès' });
    } catch (error) {
      console.error('Error deleting session:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'En cours', color: '#95de64', bg: 'rgba(82, 196, 26, 0.2)' },
      completed: { label: 'Terminée', color: 'rgba(255, 255, 255, 0.7)', bg: 'rgba(255, 255, 255, 0.1)' },
      pending: { label: 'Planifiée', color: '#FFD700', bg: 'rgba(24, 144, 255, 0.2)' },
      cancelled: { label: 'Annulée', color: '#ff4d4f', bg: 'rgba(255, 77, 79, 0.2)' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span style={{
        padding: '6px 16px',
        borderRadius: '20px',
        background: config.bg,
        color: config.color,
        fontSize: '13px',
        fontWeight: 600
      }}>
        {config.label}
      </span>
    );
  };

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div style={{
        background: 'rgba(26, 26, 46, 0.9)',
        borderRadius: '16px',
        padding: '24px 32px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '2px solid rgba(255, 215, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-calendar-alt" style={{ color: '#1a1a2e', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Sessions
          </h1>
        </div>

        <button
          onClick={handleCreateSession}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#1a1a2e',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s',
            boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.2)';
          }}
        >
          <i className="fas fa-plus"></i>
          Nouvelle Session
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <SessionKPICard
          title="Total Sessions"
          value={sessions.length}
          icon="fa-calendar-check"
          color="#1890ff"
        />
        <SessionKPICard
          title="Sessions Actives"
          value={sessions.filter(s => s.status === 'active').length}
          icon="fa-play-circle"
          color="#52c41a"
        />
        <SessionKPICard
          title="Total Participants"
          value={sessions.reduce((acc, s) => acc + (s.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0), 0)}
          icon="fa-users"
          color="#faad14"
        />
        <SessionKPICard
          title="Total Gagnants"
          value={sessions.reduce((acc, s) => acc + (s.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0), 0)}
          icon="fa-trophy"
          color="#722ed1"
        />
      </div>

      <div style={{
        background: 'rgba(26, 26, 46, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(255, 215, 0, 0.3)',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '14px', fontWeight: 600, color: '#FFD700', textTransform: 'uppercase' }}>
          Filtres
        </h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-store" style={{ color: '#FFD700', fontSize: '14px' }}></i>
            <select
              value={filters.pdv}
              onChange={(e) => setFilters({ ...filters, pdv: e.target.value })}
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(26, 26, 46, 0.8)',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les points de vente</option>
              {[...new Set(sessions.map(s => s.pdv?.name || "Non assigné"))].map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-info-circle" style={{ color: '#FFD700', fontSize: '14px' }}></i>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(26, 26, 46, 0.8)',
                cursor: 'pointer'
              }}
            >
              <option value="all">Tous les statuts</option>
              <option value="active">En cours</option>
              <option value="completed">Terminée</option>
              <option value="pending">Planifiée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-calendar" style={{ color: '#FFD700', fontSize: '14px' }}></i>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              style={{
                padding: '8px 16px',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(26, 26, 46, 0.8)',
                cursor: 'pointer'
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ 
        background: 'rgba(26, 26, 46, 0.9)', 
        borderRadius: '16px', 
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(255, 215, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '40px',
              height: '40px',
              background: '#1890ff15',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fa-solid fa-list" style={{ color: '#1890ff', fontSize: '18px' }}></i>
            </div>
            Liste des Sessions ({filteredSessions.length})
          </h3>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
                <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Point de Vente</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Session</th>
                <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Date & Horaire</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Mode</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Participants</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Gagnants</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Statut</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Stock</th>
                <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.map((session, idx) => (
                <tr key={session.id} style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                  <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>{session.pdv_name || session.pdv?.name || 'Non assigné'}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{session.name}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                    {new Date(session.start_time).toLocaleDateString('fr-FR')} {new Date(session.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(session.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {getGainModeBadge(session.gain_mode)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center', color: '#1890ff', fontSize: '14px', fontWeight: 600 }}>{session.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0}</td>
                  <td style={{ padding: '16px', textAlign: 'center', color: '#52c41a', fontSize: '14px', fontWeight: 600 }}>{session.stock_allocations?.reduce((a, alloc) => a + (alloc.initial_quantity - alloc.remaining_quantity), 0) || 0}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>{getStatusBadge(session.status)}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleViewStock(session)}
                      style={{
                        padding: '6px 16px',
                        background: '#faad1415',
                        color: '#faad14',
                        border: '1px solid #faad1430',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#faad14';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#faad1415';
                        e.target.style.color = '#faad14';
                      }}
                    >
                      <i className="fas fa-boxes"></i>
                      Stock
                    </button>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleSession(session.id)}
                        style={{
                          padding: '6px 12px',
                          background: session.status === "active" ? '#f6ffed' : '#fff1f0',
                          color: session.status === "active" ? '#52c41a' : '#ff4d4f',
                          border: `1px solid ${session.status === "active" ? '#b7eb8f' : '#ffa39e'}`,
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className={`fas fa-${session.status === "active" ? 'toggle-on' : 'toggle-off'}`}></i>
                        {session.status === "active" ? 'Activé' : 'Désactivé'}
                      </button>
                      <button
                        onClick={() => handleViewSession(session)}
                        style={{
                          padding: '6px 12px',
                          background: '#1890ff15',
                          color: '#1890ff',
                          border: '1px solid #1890ff30',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#ff4d4f15',
                          color: '#ff4d4f',
                          border: '1px solid #ff4d4f30',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#ff4d4f';
                          e.target.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#ff4d4f15';
                          e.target.style.color = '#ff4d4f';
                        }}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <SessionModal
          session={selectedSession}
          onClose={() => setShowModal(false)}
          onSave={async (newSession) => {
            // Recharger les sessions depuis le serveur pour avoir pdv_name
            await loadSessions();
            setShowModal(false);
          }}
        />
      )}

      {showStockModal && (
        <StockModal 
          session={selectedSession}
          onClose={() => setShowStockModal(false)}
          onSave={(updatedSession) => {
            setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));
            setShowStockModal(false);
          }}
        />
      )}
    </div>
  );
};

const SessionKPICard = ({ title, value, icon, color }) => (
  <div style={{ 
    background: '#ffffff', 
    borderRadius: '16px', 
    padding: '20px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    border: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }}>
    <div style={{ 
      width: '52px',
      height: '52px',
      background: color + '15',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <i className={`fa-solid ${icon}`} style={{ color: color, fontSize: '24px' }}></i>
    </div>
    <div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#ffffff', lineHeight: 1.2 }}>
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
        {title}
      </div>
    </div>
  </div>
);

const SessionModal = ({ session, onClose, onSave }) => {
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [formData, setFormData] = useState({
    name: session?.name || '',
    pdv_id: session?.pdv_id || '',
    start_time: session?.start_time ? new Date(session.start_time).toISOString().slice(0, 16) : '',
    end_time: session?.end_time ? new Date(session.end_time).toISOString().slice(0, 16) : '',
    gain_mode: session?.gain_mode || 'auto',
    ratio_value: session?.ratio_value || 5,
    percentage_value: session?.percentage_value || 80,
    stock_allocations: session?.stock_allocations || {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pdvsRes, prizesRes] = await Promise.all([
        api.get('/admin/pdv'),
        api.get('/admin/prizes')
      ]);
      setPdvs(pdvsRes.data);
      setPrizes(prizesRes.data);

      // Initialiser les stocks si création
      if (!session && Object.keys(formData.stock_allocations).length === 0) {
        const initialStock = {};
        prizesRes.data.forEach(p => {
          initialStock[p.id] = p.prize_type.includes('try_again') ? 999 : 10;
        });
        setFormData({ ...formData, stock_allocations: initialStock });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Préparer les allocations de stock pour l'API
    const stock_allocations = Object.entries(formData.stock_allocations).map(([prizeId, quantity]) => ({
      prize_config_id: parseInt(prizeId),
      initial_quantity: parseInt(quantity)
    }));

    const sessionData = {
      name: formData.name,
      pdv_id: parseInt(formData.pdv_id),
      start_time: new Date(formData.start_time).toISOString(),
      end_time: new Date(formData.end_time).toISOString(),
      gain_mode: formData.gain_mode,
      stock_allocations
    };

    // N'inclure ratio_value que si le mode est 'ratio'
    if (formData.gain_mode === 'ratio') {
      sessionData.ratio_value = formData.ratio_value;
    }

    // N'inclure percentage_value que si le mode est 'pourcentage'
    if (formData.gain_mode === 'pourcentage') {
      sessionData.percentage_value = formData.percentage_value;
    }

    console.log('Données de session à envoyer:', sessionData);

    try {
      if (session) {
        await api.put(`/admin/sessions/${session.id}`, sessionData);
      } else {
        await api.post('/admin/sessions', sessionData);
      }
      onSave(sessionData);
    } catch (error) {
      const errorData = error.response?.data;
      console.error('Erreur complète:', errorData);

      let errorMessage = 'Erreur lors de la création de la session';
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return `${err.loc?.join('.') || 'Champ'}: ${err.msg}`;
            return JSON.stringify(err);
          }).join('\n');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }

      alert(errorMessage);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgba(26, 26, 46, 0.95)',
        borderRadius: '16px',
        padding: '32px',
        width: '100%',
        maxWidth: '550px',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '2px solid rgba(255, 215, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>
            {session ? 'Modifier la Session' : 'Nouvelle Session'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>
              <i className="fas fa-store" style={{ marginRight: '8px', color: '#FFD700' }}></i>
              Point de Vente
            </label>
            <select
              value={formData.pdv_id}
              onChange={(e) => setFormData({ ...formData, pdv_id: parseInt(e.target.value) })}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(26, 26, 46, 0.8)',
                outline: 'none'
              }}
            >
              <option value="">Sélectionner un point de vente</option>
              {pdvs.map(pdv => (
                <option key={pdv.id} value={pdv.id}>{pdv.name} ({pdv.city?.name})</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>
              <i className="fas fa-bolt" style={{ marginRight: '8px', color: '#FFD700' }}></i>
              Nom de la session
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Session Promo - Janvier"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(26, 26, 46, 0.8)',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>
                <i className="fas fa-play" style={{ marginRight: '8px', color: '#FFD700' }}></i>
                Début
              </label>
              <input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#ffffff',
                  background: 'rgba(26, 26, 46, 0.8)',
                  outline: 'none'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>
                <i className="fas fa-stop" style={{ marginRight: '8px', color: '#FFD700' }}></i>
                Fin
              </label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#ffffff',
                  background: 'rgba(26, 26, 46, 0.8)',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>
              <i className="fas fa-cog" style={{ marginRight: '8px', color: '#FFD700' }}></i>
              Mode de gain
            </label>
            <select
              value={formData.gain_mode}
              onChange={(e) => setFormData({ ...formData, gain_mode: e.target.value })}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#ffffff',
                background: 'rgba(26, 26, 46, 0.8)',
                outline: 'none'
              }}
            >
              <option value="auto">Automatique - Distribution basée sur les quotas</option>
              <option value="ratio">Ratio - 1 gagnant tous les X participants</option>
              <option value="force">Forcé - Instant gagnant programmé (gros lots)</option>
              <option value="pourcentage">Pourcentage - % global de gains basé sur les stocks alloués</option>
            </select>
            {formData.gain_mode === 'ratio' && (
              <div style={{ marginTop: '8px' }}>
                <input
                  type="number"
                  value={formData.ratio_value}
                  onChange={(e) => setFormData({ ...formData, ratio_value: parseInt(e.target.value) })}
                  min="1"
                  placeholder="Ex: 5 = 1 gagnant tous les 5"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#ffffff',
                    background: 'rgba(26, 26, 46, 0.8)',
                    outline: 'none'
                  }}
                />
              </div>
            )}
            {formData.gain_mode === 'pourcentage' && (
              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'rgba(255, 215, 0, 0.8)' }}>
                  Pourcentage global de gains (ex: 80 = 80% des lots alloués seront gagnés)
                </label>
                <input
                  type="number"
                  value={formData.percentage_value}
                  onChange={(e) => setFormData({ ...formData, percentage_value: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  placeholder="Ex: 80 = 80% de gains"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#ffffff',
                    background: 'rgba(26, 26, 46, 0.8)',
                    outline: 'none'
                  }}
                />
              </div>
            )}
            {formData.gain_mode === 'force' && (
              <div style={{ marginTop: '8px', padding: '12px', background: 'rgba(255, 215, 0, 0.15)', borderRadius: '8px', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                <div style={{ fontSize: '12px', color: '#FFD700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="fas fa-info-circle"></i>
                  <span>Ce mode permet de programmer des gains forcés via le module Injection pour les gros lots (Amitié, Aventure)</span>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>
              <i className="fas fa-boxes" style={{ marginRight: '8px', color: '#FFD700' }}></i>
              Allocation des Stocks
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {prizes.filter(p => !p.prize_type.includes('try_again')).map(prize => (
                <div key={prize.id} style={{ border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', padding: '12px', background: 'rgba(26, 26, 46, 0.5)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      background: prize.color,
                      borderRadius: '50%'
                    }}></div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#ffffff' }}>
                      {prize.name}
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)' }}>{prize.prize_label}</div>
                    </div>
                  </div>
                  <input
                    type="number"
                    value={formData.stock_allocations[prize.id] || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      stock_allocations: { ...formData.stock_allocations, [prize.id]: parseInt(e.target.value) || 0 }
                    })}
                    min="0"
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#ffffff',
                      background: 'rgba(26, 26, 46, 0.8)',
                      outline: 'none'
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '8px' }}>
              * Les Try Again sont illimités et n'ont pas besoin de stock
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'transparent',
                color: '#FFD700',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                color: '#1a1a2e',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {session ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StockModal = ({ session, onClose, onSave }) => {
  const [prizes, setPrizes] = useState([]);
  const [stock, setStock] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPrizesAndStock();
  }, [session]);

  const loadPrizesAndStock = async () => {
    try {
      const prizesRes = await api.get('/admin/prizes');
      setPrizes(prizesRes.data);

      // Convertir stock_allocations en objet simple pour l'édition
      const stockObj = {};
      if (session?.stock_allocations) {
        session.stock_allocations.forEach(alloc => {
          stockObj[alloc.prize_config_id] = alloc.remaining_quantity;
        });
      } else {
        // Initialiser à 0 pour tous les lots
        prizesRes.data.forEach(p => {
          stockObj[p.id] = 0;
        });
      }
      setStock(stockObj);
    } catch (error) {
      console.error('Error loading prizes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Mettre à jour chaque allocation de stock
      for (const [prizeId, quantity] of Object.entries(stock)) {
        await api.put(`/admin/sessions/${session.id}/stocks/${prizeId}`, quantity);
      }
      onSave({ ...session, stock });
    } catch (error) {
      alert('Erreur lors de la mise à jour des stocks');
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ background: '#fff', padding: '32px', borderRadius: '16px' }}>Chargement...</div>
      </div>
    );
  }

  // Filtrer les lots non try_again
  const displayPrizes = prizes.filter(p => !p.prize_type.includes('try_again'));

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '32px',
        width: '100%',
        maxWidth: '450px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="fas fa-boxes" style={{ color: '#faad14' }}></i>
            Allocation des Stocks
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              color: '#8c8c8c'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div style={{ background: '#f9f9f9', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '4px' }}>Session</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>{session?.name}</div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff', marginBottom: '16px' }}>
            Quantité disponible par lot
          </div>
          {displayPrizes.map((prize) => (
            <div key={prize.id} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              background: '#fafafa',
              borderRadius: '12px',
              marginBottom: '12px',
              border: '2px solid rgba(255, 215, 0, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: prize.color + '15',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fa-solid fa-medal" style={{ color: prize.color, fontSize: '18px' }}></i>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>{prize.name}</div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{prize.prize_label}</div>
                </div>
              </div>
              <input
                type="number"
                value={stock[prize.id] || 0}
                onChange={(e) => setStock({ ...stock, [prize.id]: parseInt(e.target.value) || 0 })}
                min="0"
                style={{
                  width: '100px',
                  padding: '8px 12px',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#ffffff',
                  textAlign: 'center',
                  outline: 'none'
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: '#fff',
              color: '#595959',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            style={{
              flex: 1,
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <i className="fas fa-save" style={{ marginRight: '8px' }}></i>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MODULE LOTS (PRIZES) ============
const PrizesContent = ({ message, setMessage }) => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrize, setEditingPrize] = useState(null);

  useEffect(() => {
    loadPrizes();
  }, []);

  const loadPrizes = async () => {
    try {
      const response = await api.get('/admin/prizes');
      setPrizes(response.data);
    } catch (error) {
      console.error('Error loading prizes:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des lots' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (prizeData) => {
    try {
      await api.put(`/admin/prizes/${editingPrize.id}`, prizeData);
      setMessage({ type: 'success', text: 'Lot mis à jour avec succès' });
      setEditingPrize(null);
      loadPrizes();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la mise à jour' });
    }
  };

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center' }}>Chargement...</div>;
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-gift" style={{ color: '#1a1a2e', fontSize: '18px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Configuration des Lots
          </h1>
        </div>
      </div>

      <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Segment</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Émotion</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Lot</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Couleur</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Injection Admin</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {prizes.map((prize, idx) => (
              <tr key={prize.id} style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <td style={{ padding: '16px', fontWeight: 600, color: '#ffffff' }}>{prize.segment_order}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>{prize.name}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>{prize.prize_label}</td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ width: '30px', height: '30px', background: prize.color, borderRadius: '50%', margin: '0 auto' }}></div>
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  {prize.requires_admin_injection ? (
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: 'rgba(250, 140, 22, 0.2)', color: '#FFD700', fontSize: '12px', fontWeight: 600 }}>
                      Oui
                    </span>
                  ) : (
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: 'rgba(255, 215, 0, 0.2)', color: '#FFD700', fontSize: '12px' }}>
                      Non
                    </span>
                  )}
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <button
                    onClick={() => setEditingPrize(prize)}
                    style={{
                      padding: '6px 16px',
                      background: 'rgba(255, 215, 0, 0.15)',
                      color: '#FFD700',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 600
                    }}
                  >
                    <i className="fas fa-edit"></i> Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingPrize && (
        <PrizeEditModal
          prize={editingPrize}
          onClose={() => setEditingPrize(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const PrizeEditModal = ({ prize, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: prize.name,
    prize_label: prize.prize_label,
    description: prize.description || '',
    color: prize.color,
    icon: prize.icon || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'rgba(26, 26, 46, 0.95)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>Modifier le Lot</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Émotion</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Lot</label>
            <input
              type="text"
              value={formData.prize_label}
              onChange={(e) => setFormData({ ...formData, prize_label: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Couleur</label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              style={{ width: '100%', height: '50px', padding: '8px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, padding: '12px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a2e', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ MODULE INJECTION (GROS LOTS) ============
const InjectionContent = ({ message, setMessage }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [forcedWins, setForcedWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await api.get('/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadForcedWins = async (sessionId) => {
    try {
      const response = await api.get(`/admin/sessions/${sessionId}/forced-wins`);
      setForcedWins(response.data);
    } catch (error) {
      console.error('Error loading forced wins:', error);
    }
  };

  const handleSessionChange = (sessionId) => {
    setSelectedSession(sessionId);
    if (sessionId) loadForcedWins(sessionId);
  };

  const handleDelete = async (forcedWinId) => {
    if (!window.confirm('Supprimer cette injection ?')) return;

    try {
      await api.delete(`/admin/forced-wins/${forcedWinId}`);
      setMessage({ type: 'success', text: 'Injection supprimée' });
      if (selectedSession) loadForcedWins(selectedSession);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-syringe" style={{ color: '#1a1a2e', fontSize: '18px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Injection des Gros Lots
          </h1>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
        <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>Sélectionner une Session</h3>
          <select
            value={selectedSession || ''}
            onChange={(e) => handleSessionChange(parseInt(e.target.value) || null)}
            style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
          >
            <option value="">-- Choisir une session --</option>
            {sessions.map(s => (
              <option key={s.id} value={s.id}>{s.name} ({s.pdv?.name})</option>
            ))}
          </select>

          {selectedSession && (
            <button
              onClick={() => setShowModal(true)}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                color: '#1a1a2e',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>
              Nouvelle Injection
            </button>
          )}
        </div>

        <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
            Injections Programmées {forcedWins.length > 0 && `(${forcedWins.length})`}
          </h3>

          {!selectedSession ? (
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', padding: '32px' }}>
              Sélectionnez une session pour voir les injections
            </p>
          ) : forcedWins.length === 0 ? (
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', padding: '32px' }}>
              Aucune injection programmée pour cette session
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {forcedWins.map(fw => (
                <div key={fw.id} style={{
                  padding: '16px',
                  background: 'rgba(26, 26, 46, 0.5)',
                  borderRadius: '12px',
                  border: '2px solid rgba(255, 215, 0, 0.3)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#ffffff' }}>
                      {fw.prize_config?.prize_label || 'Lot inconnu'}
                    </div>
                    <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      {new Date(fw.scheduled_date).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {fw.is_triggered ? (
                      <span style={{ padding: '4px 12px', borderRadius: '20px', background: '#f6ffed', color: '#52c41a', fontSize: '12px', fontWeight: 600 }}>
                        Déclenché
                      </span>
                    ) : (
                      <span style={{ padding: '4px 12px', borderRadius: '20px', background: '#e6f7ff', color: '#1890ff', fontSize: '12px', fontWeight: 600 }}>
                        En attente
                      </span>
                    )}
                    <button
                      onClick={() => handleDelete(fw.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#fff1f0',
                        color: '#ff4d4f',
                        border: '1px solid #ffa39e',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                      title={fw.is_triggered ? 'Supprimer cette injection (déclenchée)' : 'Supprimer cette injection'}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <InjectionModal
          sessionId={selectedSession}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            loadForcedWins(selectedSession);
            setMessage({ type: 'success', text: 'Injection programmée avec succès' });
          }}
        />
      )}
    </div>
  );
};

const InjectionModal = ({ sessionId, onClose, onSave }) => {
  const [prizes, setPrizes] = useState([]);
  const [formData, setFormData] = useState({
    session_id: sessionId,
    prize_config_id: '',
    scheduled_date: ''
  });

  useEffect(() => {
    loadPrizes();
  }, []);

  const loadPrizes = async () => {
    try {
      const response = await api.get('/admin/prizes');
      setPrizes(response.data.filter(p => p.requires_admin_injection));
    } catch (error) {
      console.error('Error loading prizes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/forced-wins', {
        session_id: formData.session_id,
        prize_config_id: formData.prize_config_id,
        scheduled_date: formData.scheduled_date
      });
      onSave();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '450px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
            <i className="fas fa-syringe" style={{ color: '#722ed1', marginRight: '8px' }}></i>
            Nouvelle Injection
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#8c8c8c' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Lot à injecter</label>
            <select
              value={formData.prize_config_id}
              onChange={(e) => setFormData({ ...formData, prize_config_id: parseInt(e.target.value) })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            >
              <option value="">-- Choisir un lot --</option>
              {prizes.map(p => (
                <option key={p.id} value={p.id}>{p.prize_label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Date du gain</label>
            <input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ background: '#fff7e6', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#d46b08' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            L'injection sera valide pour toute la journée sélectionnée.
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, padding: '12px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a2e', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Programmer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ MODULE UTILISATEURS ============
const UsersContent = ({ message, setMessage }) => {
  const [users, setUsers] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filters, setFilters] = useState({ role: '', pdv_id: '' });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.role) params.role = filters.role;
      if (filters.pdv_id) params.pdv_id = filters.pdv_id;

      const [usersRes, pdvsRes] = await Promise.all([
        api.get('/admin/users', { params }),
        api.get('/admin/pdv')
      ]);
      setUsers(usersRes.data);
      setPdvs(pdvsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;

    try {
      await api.delete(`/admin/users/${userId}`);
      setMessage({ type: 'success', text: 'Utilisateur supprimé' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { label: 'Super Admin', color: '#FFD700', bg: 'rgba(114, 46, 209, 0.2)' },
      admin: { label: 'Admin', color: '#FFD700', bg: 'rgba(24, 144, 255, 0.2)' },
      hotesse: { label: 'Hôtesse', color: '#95de64', bg: 'rgba(82, 196, 26, 0.2)' }
    };
    const c = config[role] || config.hotesse;
    return (
      <span style={{ padding: '4px 12px', borderRadius: '20px', background: c.bg, color: c.color, fontSize: '12px', fontWeight: 600 }}>
        {c.label}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: '#FFD700' }}>Chargement...</div>;
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px 32px', marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-users" style={{ color: '#1a1a2e', fontSize: '18px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Utilisateurs
          </h1>
        </div>
        <button
          onClick={handleCreate}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#1a1a2e',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>
          Nouvel Utilisateur
        </button>
      </div>

      <div style={{ background: 'rgba(26, 26, 46, 0.9)', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            style={{ padding: '8px 16px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
          >
            <option value="">Tous les rôles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="hotesse">Hôtesse</option>
          </select>

          <select
            value={filters.pdv_id}
            onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
            style={{ padding: '8px 16px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
          >
            <option value="">Tous les PDV</option>
            {pdvs.map(pdv => (
              <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Utilisateur</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Email</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Rôle</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>PDV</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Statut</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: '32px', textAlign: 'center', color: '#8c8c8c' }}>Aucun utilisateur trouvé</td></tr>
            ) : users.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 600 }}>{user.full_name || user.username}</div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>@{user.username}</div>
                </td>
                <td style={{ padding: '16px', fontSize: '14px' }}>{user.email}</td>
                <td style={{ padding: '16px' }}>{getRoleBadge(user.role)}</td>
                <td style={{ padding: '16px', fontSize: '14px' }}>{user.pdv_name || '-'}</td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  {user.is_active ? (
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: '#f6ffed', color: '#52c41a', fontSize: '12px', fontWeight: 600 }}>Actif</span>
                  ) : (
                    <span style={{ padding: '4px 12px', borderRadius: '20px', background: '#fff1f0', color: '#ff4d4f', fontSize: '12px', fontWeight: 600 }}>Inactif</span>
                  )}
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleEdit(user)}
                      style={{ padding: '6px 12px', background: '#1890ff15', color: '#1890ff', border: '1px solid #1890ff30', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      style={{ padding: '6px 12px', background: '#fff1f0', color: '#ff4d4f', border: '1px solid #ffa39e', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <UserModal
          user={editingUser}
          pdvs={pdvs}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            loadData();
            setMessage({ type: 'success', text: editingUser ? 'Utilisateur mis à jour' : 'Utilisateur créé' });
          }}
        />
      )}
    </div>
  );
};

const UserModal = ({ user, pdvs, onClose, onSave }) => {
  const [formData, setFormData] = useState(user || {
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'hotesse',
    pdv_id: '',
    is_active: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Préparer les données à envoyer
      const dataToSend = { ...formData };

      // Si c'est une modification et le mot de passe est vide, ne pas l'envoyer
      if (user && !dataToSend.password) {
        delete dataToSend.password;
      }

      // Convertir pdv_id vide en null
      if (!dataToSend.pdv_id) {
        dataToSend.pdv_id = null;
      }

      // Log pour débogage
      console.log('Données envoyées:', dataToSend);

      if (user) {
        await api.put(`/admin/users/${user.id}`, dataToSend);
      } else {
        await api.post('/admin/users', dataToSend);
      }
      onSave();
    } catch (error) {
      const errorData = error.response?.data;
      console.error('Erreur complète:', errorData);

      // Afficher les détails de l'erreur
      let errorMessage = 'Erreur lors de l\'enregistrement';
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          // Si c'est un tableau d'erreurs de validation
          errorMessage = errorData.detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return `${err.loc?.join('.') || 'Champ'}: ${err.msg}`;
            return JSON.stringify(err);
          }).join('\n');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }

      alert(errorMessage);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'rgba(26, 26, 46, 0.95)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', border: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#ffffff' }}>
            {user ? 'Modifier' : 'Créer'} un Utilisateur
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>Nom d'utilisateur</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>Nom complet</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>Mot de passe {user && '(laisser vide pour garder)'}</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              placeholder={user ? 'Laisser vide pour ne pas changer' : ''}
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>Rôle</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            >
              <option value="hotesse">Hôtesse</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#FFD700' }}>Point de Vente</label>
            <select
              value={formData.pdv_id}
              onChange={(e) => setFormData({ ...formData, pdv_id: e.target.value ? parseInt(e.target.value) : '' })}
              style={{ width: '100%', padding: '12px', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', background: 'rgba(26, 26, 46, 0.8)', color: '#ffffff' }}
            >
              <option value="">Aucun</option>
              {pdvs.map(pdv => (
                <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>Compte actif</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, padding: '12px', background: 'transparent', color: '#FFD700', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1a1a2e', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
            >
              {user ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ParticipantsContent = ({ message, setMessage }) => {
  const [participations, setParticipations] = useState([]);
  const [cities, setCities] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [localMessage, setLocalMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    city_id: null,
    pdv_id: null,
    session_id: null,
    prize_won: null,
    date_from: null,
    date_to: null
  });

  const loadParticipations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.city_id && filters.city_id !== '') params.city_id = parseInt(filters.city_id);
      if (filters.pdv_id && filters.pdv_id !== '') params.pdv_id = parseInt(filters.pdv_id);
      if (filters.session_id && filters.session_id !== '') params.session_id = parseInt(filters.session_id);
      if (filters.prize_won && filters.prize_won !== '') {
        if (filters.prize_won === 'null') {
          params.played = false; // Non joué
        } else {
          params.prize_won = filters.prize_won;
        }
      }
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      console.log('Loading participations with params:', params);
      const response = await api.get('/admin/participations', { params });
      setParticipations(response.data);
    } catch (error) {
      console.error('Error loading participations:', error);
      setLocalMessage({ type: 'danger', text: 'Erreur lors du chargement des participants' });
    } finally {
      setLoading(false);
    }
  }, [filters.city_id, filters.pdv_id, filters.session_id, filters.prize_won, filters.date_from, filters.date_to]);

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadPdvs = useCallback(async () => {
    try {
      const response = await api.get('/admin/pdv');
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const response = await api.get('/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  }, []);

  useEffect(() => {
    loadCities();
    loadPdvs();
    loadSessions();
  }, []);

  // Recharger les PDV quand la ville change
  useEffect(() => {
    if (filters.city_id) {
      loadPdvs();
    }
  }, [filters.city_id, loadPdvs]);

  useEffect(() => {
    loadParticipations();
  }, [loadParticipations]);

  const handleSendWhatsApp = async () => {
    if (!selectedParticipation || !whatsappMessage.trim()) {
      setLocalMessage({ type: 'danger', text: 'Veuillez entrer un message' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('message', whatsappMessage);

      console.log('Sending WhatsApp to participation:', selectedParticipation.id);
      const response = await api.post(`/admin/send-whatsapp/${selectedParticipation.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('WhatsApp URL:', response.data.whatsapp_url);

      // Ouvrir le lien WhatsApp
      const newWindow = window.open(response.data.whatsapp_url, '_blank');

      if (newWindow) {
        setLocalMessage({ type: 'success', text: 'Lien WhatsApp ouvert !' });
        setShowWhatsAppModal(false);
        setWhatsappMessage('');
        setSelectedParticipation(null);
      } else {
        setLocalMessage({ type: 'warning', text: 'Popup bloqué ! Veuillez autoriser les popups pour ce site.' });
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      setLocalMessage({ type: 'danger', text: error.response?.data?.detail || error.message || 'Erreur lors de l\'envoi' });
    }
  };

  const handleViewDetails = async (participation) => {
    try {
      const response = await api.get(`/admin/participations/${participation.id}`);
      setSelectedParticipation(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      setLocalMessage({ type: 'danger', text: 'Erreur lors du chargement des détails' });
    }
  };

  const getPrizeLabel = (prizeType) => {
    const labels = {
      'passion': 'Rose',
      'force': 'Bouteille',
      'complicite': 'Chocolats',
      'tendresse': 'Polo',
      'amitie': 'Dîner',
      'aventure': 'Week-end Kribi',
      'try_again_1': 'Try Again',
      'try_again_2': 'Try Again'
    };
    return labels[prizeType] || prizeType;
  };

  const exportToExcel = () => {
    // Préparer les données pour l'export
    const dataToExport = participations.map(p => ({
      'Date': new Date(p.created_at).toLocaleDateString('fr-FR'),
      'Heure': new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      'Participant': p.participant_name,
      'Contact': p.participant_phone,
      'Ville': p.city_name || '-',
      'PDV': p.pdv_name,
      'Hôtesse': p.hotesse_name,
      'Mode': p.play_mode === 'whatsapp' ? 'WhatsApp' : 'Direct',
      'Statut': p.played ? 'Joué' : 'En attente',
      'Lot': p.plays_history && p.plays_history.length > 0 ? p.plays_history.map(play => `T${play.play_number}: ${getPrizeLabel(play.prize_type)}`).join(' • ') : (p.prize_won ? getPrizeLabel(p.prize_won) : '-'),
      'Code lot': p.prize_code || '-',
      'Livré': p.prize_delivered ? 'Oui' : 'Non',
      'Bouteilles': p.bottles_count,
      'Photo ticket': p.ticket_photo_path ? 'Oui' : 'Non'
    }));

    // Créer le workbook
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Participants');

    // Générer le fichier
    XLSX.writeFile(workbook, `participants_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div style={{ flex: 1, padding: '32px', background: 'transparent' }}>
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-warning" role="status"></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {localMessage.text && (
        <div className={`alert alert-${localMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
          {localMessage.text}
          <button type="button" className="btn-close" onClick={() => setLocalMessage({ type: '', text: '' })}></button>
        </div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#1a1a2e', border: '2px solid #FFD700' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fab fa-whatsapp text-success me-2"></i>
                  Envoyer message WhatsApp
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowWhatsAppModal(false)}></button>
              </div>
              <div className="modal-body">
                {localMessage.text && (
                  <div className={`alert alert-${localMessage.type === 'success' ? 'success' : localMessage.type === 'warning' ? 'warning' : 'danger'} alert-dismissible fade show`} role="alert">
                    {localMessage.text}
                    <button type="button" className="btn-close" onClick={() => setLocalMessage({ type: '', text: '' })}></button>
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label text-light">Participant</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedParticipation.participant_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Téléphone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedParticipation.participant_phone}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={whatsappMessage}
                    onChange={(e) => setWhatsappMessage(e.target.value)}
                    placeholder="Entrez votre message..."
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWhatsAppModal(false)}>Annuler</button>
                <button type="button" className="btn btn-success" onClick={handleSendWhatsApp}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ background: '#1a1a2e', border: '2px solid #FFD700' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-user me-2" style={{ color: '#FFD700' }}></i>
                  Détails du participant
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Nom</label>
                    <div className="text-white fw-semibold">{selectedParticipation.participant?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Téléphone</label>
                    <div className="text-white font-monospace">{selectedParticipation.participant?.phone}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">PDV</label>
                    <div className="text-white">{selectedParticipation.pdv?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Hôtesse</label>
                    <div className="text-white">{selectedParticipation.hotesse?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Session</label>
                    <div className="text-white">{selectedParticipation.session?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Mode de jeu</label>
                    <div className="text-white">{selectedParticipation.play_mode === 'whatsapp' ? 'WhatsApp' : 'Direct'}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Date d'enregistrement</label>
                    <div className="text-white">{new Date(selectedParticipation.created_at).toLocaleString('fr-FR')}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Nombre de bouteilles</label>
                    <div className="text-white">{selectedParticipation.bottles_count}</div>
                  </div>
                  {selectedParticipation.played && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Date du jeu</label>
                        <div className="text-white">{selectedParticipation.played_at ? new Date(selectedParticipation.played_at).toLocaleString('fr-FR') : '-'}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Lot gagné</label>
                        <div className="text-white">{selectedParticipation.prize_label || '-'}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Code lot</label>
                        <div className="text-white font-monospace">{selectedParticipation.prize_code || '-'}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Statut livraison</label>
                        <div className="text-white">
                          {selectedParticipation.prize_delivered ? (
                            <span className="badge bg-success">Livré</span>
                          ) : (
                            <span className="badge bg-secondary">Non livré</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {selectedParticipation.ticket_photo_url && (
                    <div className="col-12 mb-3">
                      <label className="form-label text-light small">Photo du ticket</label>
                      <div>
                        <img
                          src={selectedParticipation.ticket_photo_url}
                          alt="Ticket"
                          style={{ maxWidth: '300px', borderRadius: '8px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if(e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                          }}
                          onLoaded={(e) => {
                            if (e.target.src) {
                              e.target.style.display = 'block';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'none';
                              }
                            }
                          }}
                        />
                        <div style={{ display: 'none' }} className="text-center text-warning mt-2">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          Image non disponible
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-primary" onClick={() => {
                  setShowDetailsModal(false);
                  setShowWhatsAppModal(true);
                }}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer WhatsApp
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{
        background: 'rgba(26, 26, 46, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(255, 215, 0, 0.3)'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="text-white fw-bold mb-1">
              <i className="fas fa-users me-2" style={{ color: '#FFD700' }}></i>
              Gestion des Participants
            </h2>
            <p className="text-light mb-0">{participations.length} participant(s)</p>
          </div>
          <button
            className="btn btn-success"
            onClick={exportToExcel}
            disabled={participations.length === 0}
            style={{ background: '#28a745', color: '#fff', border: 'none', fontWeight: 'bold' }}
          >
            <i className="fas fa-file-excel me-2"></i>
            Exporter Excel
          </button>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.8)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label text-light small">Ville</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.city_id || ''}
                  onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Toutes les villes</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id.toString()}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">PDV</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.pdv_id || ''}
                  onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Tous</option>
                  {pdvs
                    .filter(p => !filters.city_id || p.city_id == filters.city_id)
                    .map(pdv => (
                      <option key={pdv.id} value={pdv.id.toString()}>{pdv.name}</option>
                    ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Session</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.session_id}
                  onChange={(e) => setFilters({ ...filters, session_id: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Toutes</option>
                  {sessions.map(session => (
                    <option key={session.id} value={session.id.toString()}>{session.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Lot</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.prize_won || ''}
                  onChange={(e) => setFilters({ ...filters, prize_won: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Tous</option>
                  <option value="passion">Rose</option>
                  <option value="force">Bouteille</option>
                  <option value="complicite">Chocolats</option>
                  <option value="tendresse">Polo</option>
                  <option value="amitie">Dîner</option>
                  <option value="aventure">Week-end</option>
                  <option value="try_again_1">Try Again</option>
                  <option value="try_again_2">Try Again</option>
                  <option value="null">Non joué</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Date début</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Date fin</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card border-0 shadow" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.8)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: 'rgba(26, 26, 46, 0.9)', borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
                  <tr>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Date/Heure</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Participant</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Contact</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Photo</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Ville</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>PDV</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Hôtesse</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Mode</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Statut</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Lot</th>
                    <th style={{ padding: '12px', color: '#FFD700', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {participations.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-5">
                        <i className="fas fa-users-slash fa-3x mb-3" style={{ opacity: 0.3, color: '#FFD700' }}></i>
                        <p className="text-light mb-0">Aucun participant trouvé</p>
                      </td>
                    </tr>
                  ) : participations.map((p) => (
                    <tr key={p.id} style={{ color: '#fff', cursor: 'pointer' }} onClick={() => handleViewDetails(p)}>
                      <td className="p-3">
                        <div className="small" style={{ color: '#000', fontWeight: 600 }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</div>
                        <div className="fw-semibold" style={{ color: '#000', fontWeight: 600 }}>{new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="p-3 fw-semibold">{p.participant_name}</td>
                      <td className="p-3 font-monospace">{p.participant_phone}</td>
                      <td className="p-3">
                        {p.ticket_photo_url ? (
                          <img
                            src={p.ticket_photo_url}
                            alt="Ticket"
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: '2px solid rgba(255, 215, 0, 0.3)'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(p.ticket_photo_url, '_blank');
                            }}
                            title="Cliquez pour agrandir"
                          />
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="badge bg-warning text-dark" style={{ fontSize: '11px' }}>
                          {p.city_name || '-'}
                        </span>
                      </td>
                      <td className="p-3">{p.pdv_name}</td>
                      <td className="p-3">{p.hotesse_name}</td>
                      <td className="p-3">
                        <span className={`badge ${p.play_mode === 'whatsapp' ? 'bg-success' : 'bg-primary'}`}>
                          <i className={`fab fa-${p.play_mode === 'whatsapp' ? 'whatsapp' : 'chrome'} me-1`}></i>
                          {p.play_mode === 'whatsapp' ? 'WhatsApp' : 'Direct'}
                        </span>
                      </td>
                      <td className="p-3">
                        {p.played ? (
                          <span className="badge bg-success">
                            <i className="fas fa-check-circle me-1"></i>Joué
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            <i className="fas fa-clock me-1"></i>En attente
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {p.plays_history && p.plays_history.length > 0 ? (
                          <div style={{ fontSize: '12px', lineHeight: '1.8' }}>
                            {p.plays_history.map((play, idx) => (
                              <span key={idx} style={{ display: 'inline-block', marginRight: idx < p.plays_history.length - 1 ? '6px' : '0' }}>
                                <span style={{
                                  color: '#000',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                  padding: '5px 9px',
                                  borderRadius: '6px',
                                  background: idx % 2 === 0 ? '#e3f2fd' : '#f3e5f5',
                                  border: '1px solid #dee2e6',
                                  whiteSpace: 'nowrap'
                                }}>
                                  T{play.play_number}: {getPrizeLabel(play.prize_type)}
                                </span>
                              </span>
                            ))}
                          </div>
                        ) : p.prize_won ? (
                          <span className="badge bg-info">{getPrizeLabel(p.prize_won)}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-success me-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedParticipation(p);
                            setShowWhatsAppModal(true);
                          }}
                          title="Envoyer WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(p);
                          }}
                          title="Voir détails"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PDVsContent = ({ message, setMessage }) => {
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPDV, setSelectedPDV] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city_id: '',
    has_big_screen: false
  });

  useEffect(() => {
    loadPDVs();
    loadCities();
  }, []);

  const loadPDVs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pdv');
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des points de vente' });
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleCreatePDV = () => {
    setSelectedPDV(null);
    setFormData({ name: '', code: '', address: '', city_id: '', has_big_screen: false });
    setShowModal(true);
  };

  const handleEditPDV = (pdv) => {
    setSelectedPDV(pdv);
    setFormData({
      name: pdv.name,
      code: pdv.code,
      address: pdv.address || '',
      city_id: pdv.city_id?.toString() || '',
      has_big_screen: pdv.has_big_screen || false
    });
    setShowModal(true);
  };

  const handleSavePDV = async () => {
    try {
      if (selectedPDV) {
        await api.put(`/admin/pdv/${selectedPDV.id}`, formData);
        setMessage({ type: 'success', text: 'Point de vente mis à jour avec succès' });
      } else {
        await api.post('/admin/pdv', formData);
        setMessage({ type: 'success', text: 'Point de vente créé avec succès' });
      }
      setShowModal(false);
      loadPDVs();
    } catch (error) {
      console.error('Error saving PDV:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleDeletePDV = async (pdvId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce point de vente ?')) {
      return;
    }

    try {
      await api.delete(`/admin/pdv/${pdvId}`);
      setPdvs(pdvs.filter(p => p.id !== pdvId));
      setMessage({ type: 'success', text: 'Point de vente supprimé avec succès' });
    } catch (error) {
      console.error('Error deleting PDV:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, padding: '32px', background: 'transparent' }}>
        <div style={{ color: '#ffffff' }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: '32px', background: 'transparent', overflowY: 'auto' }}>
      {message.text && (
        <div style={{
          padding: '12px 24px',
          background: message.type === 'error' ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)',
          border: `1px solid ${message.type === 'error' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(82, 196, 26, 0.4)'}`,
          borderRadius: '8px',
          marginBottom: '24px',
          color: message.type === 'error' ? '#ff7875' : '#95de64',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas fa-${message.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {message.text}
        </div>
      )}

      <div style={{
        background: 'rgba(26, 26, 46, 0.9)',
        borderRadius: '16px',
        padding: '24px 32px',
        marginBottom: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '2px solid rgba(255, 215, 0, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-store" style={{ color: '#1a1a2e', fontSize: '20px' }}></i>
          </div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            Points de Vente
          </h1>
        </div>

        <button
          onClick={handleCreatePDV}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#1a1a2e',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s',
            boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.2)';
          }}
        >
          <i className="fas fa-plus"></i>
          Nouveau PDV
        </button>
      </div>

      <div style={{
        background: 'rgba(26, 26, 46, 0.9)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        border: '2px solid rgba(255, 215, 0, 0.3)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Nom</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Code</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Ville</th>
              <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Adresse</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Statut</th>
              <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pdvs.map((pdv) => (
              <tr key={pdv.id} style={{ borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>{pdv.name}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{pdv.code}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{pdv.city?.name || '-'}</td>
                <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>{pdv.address || '-'}</td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <span style={{
                    padding: '6px 16px',
                    borderRadius: '20px',
                    background: pdv.is_active ? 'rgba(82, 196, 26, 0.2)' : 'rgba(255, 77, 79, 0.2)',
                    color: pdv.is_active ? '#95de64' : '#ff7875',
                    fontSize: '13px',
                    fontWeight: 600
                  }}>
                    {pdv.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={{ padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      onClick={() => handleEditPDV(pdv)}
                      style={{
                        padding: '6px 12px',
                        background: '#1890ff15',
                        color: '#1890ff',
                        border: '1px solid #1890ff30',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#1890ff';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#1890ff15';
                        e.target.style.color = '#1890ff';
                      }}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeletePDV(pdv.id)}
                      style={{
                        padding: '6px 12px',
                        background: '#ff4d4f15',
                        color: '#ff4d4f',
                        border: '1px solid #ff4d4f30',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#ff4d4f';
                        e.target.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#ff4d4f15';
                        e.target.style.color = '#ff4d4f';
                      }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'rgba(26, 26, 46, 0.95)',
            borderRadius: '16px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px',
            border: '2px solid rgba(255, 215, 0, 0.3)'
          }}>
            <h2 style={{ color: '#FFD700', marginBottom: '24px', fontSize: '20px' }}>
              {selectedPDV ? 'Modifier le PDV' : 'Nouveau Point de Vente'}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Nom *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Ville *</label>
                <select
                  value={formData.city_id}
                  onChange={(e) => setFormData({ ...formData, city_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Sélectionner une ville</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ color: '#fff', marginBottom: '8px', display: 'block', fontSize: '14px' }}>Adresse</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.has_big_screen}
                  onChange={(e) => setFormData({ ...formData, has_big_screen: e.target.checked })}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ color: '#fff', fontSize: '14px', margin: 0 }}>Écran géant</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSavePDV}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: '#1a1a2e',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {selectedPDV ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;